import { Button, Col, Input, Row, Select, message } from "antd";
import React, { useState, useEffect } from "react";
import "../../../styles/steps.css";
import FieldMappingTable from "../../../components/table/FieldMappingTable";
import { getMapping, getMappingNames } from "../../../service/db.service";
import {
  checkElementArrayObjArray,
  simpleArrayToTree,
} from "../../../helper/array_helper";
import NewMappingTable from "../../../components/table/NewMappingTable";

const { Option } = Select;

/**
 * TaskMapFields — mapping selection and editor inside the task creation modal.
 *
 * Props from parent (TaskModal via FileToDbTask / FileToFileTask / DbToDbTask):
 *   mappingSelection  string   "new" | "existing" | ""
 *   setMappingSelection fn
 *   mapName           string   current mapping name
 *   setMapName        fn
 *   mappedData        array    current mapping rows (persisted across re-opens)
 *   setMappedData     fn
 *   mappedList        array    list of existing mapping names
 *   setMappedList     fn
 *   fileHeader        array    source 1 column headers
 *   sqlHeader         array    source 2 column headers
 *   confirmMapFields  fn(step, tableData, mappingName, fieldTests, type)
 *   clickPrevious     fn
 */
function TaskMapFields(props) {
  // Mirror parent state locally so the component stays responsive
  const [newMapping, setNewMapping] = useState(props.mappingSelection || "");
  const [listOfMapping, setListOfMapping] = useState(props.mappedList || []);
  const [dataSource, setDataSource] = useState([]);
  const [mappingName, setMappingName] = useState(props.mapName || "");
  const [existingMappingData, setExistingMappingData] = useState(
    props.mappedData || [],
  );
  const [fieldLevelData, setFieldLevelData] = useState([]);
  const [mappingNameDisabled, setMappingNameDisabled] = useState(
    props.mappingSelection === "existing",
  );
  const [existingMappingName, setExistingMappingName] = useState(
    props.mapName || "",
  );
  const [isTableSaved, setIsTableSaved] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [mappingMismatch, setMappingMismatch] = useState(false);

  // When the modal reopens with saved state, restore local state from props
  useEffect(() => {
    if (props.mappingSelection) {
      setNewMapping(props.mappingSelection);
      setMappingNameDisabled(props.mappingSelection === "existing");
    }
    if (props.mapName) {
      setMappingName(props.mapName);
      setExistingMappingName(props.mapName);
    }
    if (props.mappedData && props.mappedData.length > 0) {
      setExistingMappingData(props.mappedData);
      setDataSource(props.mappedData);
    }
    if (props.mappedList && props.mappedList.length > 0) {
      setListOfMapping(props.mappedList);
    }
  }, []); // run only on mount — restores state when modal reopens

  // ── Confirm / save ──────────────────────────────────────────
  const onConfirmConditions = (tableData, isPresent, fieldTests, type) => {
    if (tableData.length === 0) {
      message.error("Add at least one mapping row");
      return;
    }
    if (!isPresent) {
      message.error(
        "Select at least one Unique Identifier between the two sources",
      );
      return;
    }
    props.confirmMapFields(4, tableData, mappingName, fieldTests, type);
  };

  const onClickConfirmMap = () => {
    const name = mappingName || props.mapName || "";
    if (!name.trim()) {
      message.error("Mapping name is mandatory");
      return;
    }
    if (!isTableSaved) {
      message.error("Please save the current row before continuing");
      return;
    }

    if (newMapping === "new") {
      const isPresent = dataSource.some((d) => d.rowKey === true);
      onConfirmConditions(dataSource, isPresent, fieldLevelData, "new");
    } else {
      // existing selected
      if (mappingNameDisabled) {
        // Unchanged existing mapping
        const selectedColumns = existingMappingData.map(
          (i) => i.source1 + "-" + i.rowKey,
        );
        const isPresent = existingMappingData.some((d) => d.rowKey === true);
        onConfirmConditions(
          existingMappingData,
          isPresent,
          simpleArrayToTree(selectedColumns),
          "existing",
        );
      } else {
        // Existing mapping edited
        if (
          JSON.stringify(existingMappingData) === JSON.stringify(dataSource)
        ) {
          const selectedColumns = existingMappingData.map(
            (i) => i.source1 + "-" + i.rowKey,
          );
          const isPresent = existingMappingData.some((d) => d.rowKey === true);
          onConfirmConditions(
            existingMappingData,
            isPresent,
            simpleArrayToTree(selectedColumns),
            "existing",
          );
        } else {
          if (mappingName.trim() === existingMappingName.trim()) {
            message.error("Save edited mapping with a new mapping name");
            return;
          }
          const updatedData = dataSource.map((item) => ({
            ...item,
            mappingName,
          }));
          const isPresent = updatedData.some((d) => d.rowKey === true);
          onConfirmConditions(updatedData, isPresent, fieldLevelData, "new");
        }
      }
    }
  };

  // ── Mapping type selection ──────────────────────────────────
  const handleMappingTypeSelection = (value) => {
    setExistingMappingData([]);
    setDataSource([]);
    setMappingName("");
    setNewMapping(value);
    props.setMapName("");
    props.setMappedData([]);
    props.setMappingSelection(value);
    if (value === "existing") {
      setMappingNameDisabled(true);
      getMappingAsList();
      setIsTableSaved(true);
    } else {
      setMappingMismatch(false);
      setMappingNameDisabled(false);
      setExistingMappingName("");
    }
  };

  const handleMappingNameSelection = (value) => {
    setExistingMappingName(value);
    setMappingName(value);
    props.setMapName(value);
    getMappingByName(value);
  };

  // ── Assign data source (called from table component) ────────
  // No header-match validation here: a NEW mapping's columns are picked FROM the
  // file/SQL header dropdowns, so they can't mismatch — validating them only
  // produces false positives (e.g. on an in-progress blank row). The meaningful
  // check — a saved EXISTING mapping referencing columns that aren't in the
  // current file/query — is done once at load time in getMappingByName.
  const assignDataSource = (data, selectedHeaders, isSaved) => {
    setFieldLevelData(simpleArrayToTree(selectedHeaders));
    setDataSource(data);
    setIsTableSaved(isSaved);
    // Persist to parent so re-opening shows current data
    props.setMappedData(data);
  };

  const editMappingName = () => setMappingNameDisabled(false);

  // ── Load existing mapping ───────────────────────────────────
  const getMappingByName = (name) => {
    setTableLoading(true);
    setExistingMappingData([]);
    getMapping(name)
      .then((res) => {
        const data = res.data || [];
        setExistingMappingData(data);
        props.setMappedData(data);

        // Validate the SELECTED existing mapping against the CURRENT file / SQL
        // headers. A saved mapping can reference columns that no longer exist in
        // the chosen file or query — flag it and block Confirm. Each side is
        // checked only when its headers are loaded, so a file-side mismatch is
        // caught even before a test suite is picked (and vice versa).
        const sourceOneBad =
          props.fileHeader?.length > 0 &&
          !checkElementArrayObjArray(data, props.fileHeader, "source1");
        const sourceTwoBad =
          props.sqlHeader?.length > 0 &&
          !checkElementArrayObjArray(data, props.sqlHeader, "source2");
        if (sourceOneBad || sourceTwoBad) {
          setMappingMismatch(true);
          message.error(
            "This mapping doesn't match the current file or SQL headers — some mapped columns are missing.",
          );
        } else {
          setMappingMismatch(false);
        }
        setTableLoading(false);
      })
      .catch(() => setTableLoading(false));
  };

  const getMappingAsList = () => {
    getMappingNames().then((res) => {
      setListOfMapping(res.data || []);
      props.setMappedList(res.data || []);
    });
  };

  // ── Render ──────────────────────────────────────────────────
  const currentSelection = newMapping || props.mappingSelection;

  return (
    <div className="steps-container">
      {/* Type selection */}
      <Row style={{ marginBottom: 8 }}>
        <Col span={6}>
          <p>Make a selection *</p>
        </Col>
        <Col span={12}>
          <Select
            value={
              currentSelection === "new"
                ? "Create new mapping"
                : currentSelection === "existing"
                  ? "From existing mapping"
                  : undefined
            }
            placeholder="make a selection"
            style={{ width: "100%" }}
            onChange={handleMappingTypeSelection}
          >
            <Option value="existing">From existing mapping</Option>
            <Option value="new">Create new mapping</Option>
          </Select>
        </Col>
      </Row>

      {/* Existing mapping name picker */}
      {currentSelection === "existing" && (
        <Row style={{ marginBottom: 8 }}>
          <Col span={6}>
            <p>Select Mapping *</p>
          </Col>
          <Col span={12}>
            <Select
              showSearch
              placeholder="select from existing"
              value={props.mapName || undefined}
              optionFilterProp="value"
              filterOption={(input, option) =>
                option?.value?.toLowerCase().includes(input.toLowerCase())
              }
              style={{ minWidth: "100%" }}
              onChange={handleMappingNameSelection}
            >
              {listOfMapping.map((name) => (
                <Option key={name} value={name}>
                  {name}
                </Option>
              ))}
            </Select>
          </Col>
        </Row>
      )}

      {/* Mapping name input */}
      <Row style={{ marginBottom: 8 }}>
        <Col span={6}>
          <p>Mapping Name *</p>
        </Col>
        <Col span={12}>
          <Input
            placeholder="name"
            value={props.mapName}
            disabled={mappingNameDisabled}
            onChange={(e) => {
              setMappingName(e.target.value);
              props.setMapName(e.target.value);
            }}
          />
        </Col>
      </Row>

      {/* Mapping table — new */}
      {currentSelection === "new" && (
        <Row>
          <Col span={24}>
            <FieldMappingTable
              fileHeader={props.fileHeader || []}
              sqlHeader={props.sqlHeader || []}
              mappingName={mappingName}
              assignDataSource={assignDataSource}
              initialData={props.mappedData || []}
              dataSourceOne={props.dataSourceOneColumn}
              dataSourceTwo={props.dataSourceTwoColumn}
              setIsTableSaved={setIsTableSaved}
            />
          </Col>
        </Row>
      )}

      {/* Mapping table — existing */}
      {currentSelection === "existing" && (
        <Row>
          <Col span={24}>
            <NewMappingTable
              initialData={existingMappingData}
              sqlHeader={props.sqlHeader || []}
              fileHeader={props.fileHeader || []}
              mappingName={mappingName}
              assignDataSource={assignDataSource}
              editMappingName={editMappingName}
              tableLoading={tableLoading}
              dataSourceOne={props.dataSourceOneColumn}
              dataSourceTwo={props.dataSourceTwoColumn}
              mappedData={existingMappingData}
              setMappedData={props.setMappedData}
            />
          </Col>
        </Row>
      )}

      {/* Actions */}
      <Row className="steps-header" style={{ marginTop: 16 }}>
        <Col span={6}>
          <Button type="primary" onClick={() => props.clickPrevious(2)}>
            Previous
          </Button>
        </Col>
        <Col span={6}>
          <Button
            type="primary"
            onClick={onClickConfirmMap}
            disabled={mappingMismatch}
          >
            Confirm
          </Button>
        </Col>
      </Row>
    </div>
  );
}

export default TaskMapFields;
